import React, {
  Children,
  type ComponentProps,
  type FunctionComponent,
  MouseEvent,
  useState,
  cloneElement,
  isValidElement
} from 'react';
import { BpDesignSystem } from '../../types';
import { Box } from '../Box/Box';
import { Grid, GridProps } from '../Grid/Grid';
import { Avatar, AvatarProps, AvatarSize } from '../Avatar/Avatar';
import { Transition, type AnimationOptions } from '../Transition/Transition';
import Backdrop from '@mui/material/Backdrop';
import { Popper, PopperProps } from '../Popper/Popper';
import { Fade } from '../Fade/Fade';
import { Paper } from '../Paper/Paper';
import { styled } from '../../styles';

export type GroupProps<T extends keyof JSX.IntrinsicElements | FunctionComponent = any> =
  BpDesignSystem.PropsWithChildren<{
    direction?: 'row' | 'column';
    spacing?: number;
    max?: number;
    size?: AvatarSize;
    overlap?: boolean;
    disablePopper?: boolean;
    noWrap?: boolean;
    showPopperOn?: 'click' | 'hover';
    hidePopperOn?: 'click' | 'leave';
    popperSpacing?: number;
    firstOnTop?: boolean;
    slotProps?: {
      grid?: GridProps;
      surplusWrapper?: GridProps;
      surplus?: AvatarProps;
      popper?: Partial<PopperProps>;
      popperElement?: Partial<ComponentProps<T>>;
      popperElementsAnimation?: Partial<AnimationOptions>;
    };
  }>;

const POPOVER_ID = 'hidden-elements-popover';

export function Group<T extends keyof JSX.IntrinsicElements | FunctionComponent>({
  direction,
  max = 9999,
  spacing = 0,
  size,
  slotProps,
  children,
  /**
   * elements will overlap each other
   */
  overlap,
  /**
   * use with conjunction overlap to have the first element on top of the stack
   */
  firstOnTop = true,
  noWrap = false,
  disablePopper = false,
  showPopperOn = 'hover',
  hidePopperOn = 'leave',
  popperSpacing = 3,
  ...basicProps
}: GroupProps<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const ref = React.useRef<HTMLDivElement | null>(null);

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const elements = Children.map(children, (child, index) => {
    const visible = index < max;
    return {
      visible,
      element:
        !visible && slotProps?.popperElement && isValidElement(child)
          ? cloneElement(child, slotProps?.popperElement)
          : child
    };
  });
  const visibleElements: React.ReactNode[] = elements?.filter((it) => it.visible)?.map((it) => it.element) || [];
  const hiddenElements: React.ReactNode[] = elements?.filter((it) => !it.visible)?.map((it) => it.element) || [];

  const open = Boolean(anchorEl);
  const showPopup = !disablePopper && !!hiddenElements.length;
  const columnsCount = visibleElements.length + (showPopup ? 1 : 0);
  return (
    <Box {...basicProps}>
      <Grid
        container
        direction={direction}
        spacing={!overlap ? spacing : 0}
        alignItems={direction === 'column' ? 'flex-start' : 'center'}
        flexWrap={noWrap ? 'nowrap' : 'wrap'}
        columns={noWrap ? (direction === 'row' ? columnsCount : 1) : undefined}
        sx={
          overlap
            ? (theme) => ({
                '.MuiAvatar-root': {
                  border: `2px solid ${theme.tokens.colorOnPrimary}`
                }
              })
            : undefined
        }
        {...slotProps?.grid}
      >
        {visibleElements.map((child, index) => {
          return (
            <Grid
              position="relative"
              zIndex={overlap && firstOnTop ? visibleElements.length + 1 - index : undefined}
              marginLeft={direction !== 'column' && overlap && index !== 0 ? -spacing : 0}
              marginTop={direction === 'column' && overlap && index !== 0 ? -spacing : 0}
              key={index}
              overflow="hidden"
              size={noWrap ? 1 : undefined}
              flexGrow={noWrap ? 1 : undefined}
            >
              {child}
            </Grid>
          );
        })}
        {!!hiddenElements.length && (
          <Grid
            ref={ref}
            position="relative"
            size={noWrap ? 1 : undefined}
            flexBasis={noWrap ? 'min-content' : undefined}
            marginLeft={direction !== 'column' && overlap ? -spacing : 0}
            marginTop={direction === 'column' && overlap ? -spacing : 0}
            onClick={(e) => {
              if (showPopperOn === 'click') {
                anchorEl ? handlePopoverClose() : handlePopoverOpen(e);
              }
            }}
            onMouseEnter={(e) => {
              if (showPopperOn === 'hover') {
                handlePopoverOpen(e);
              }
            }}
            style={{
              cursor: showPopperOn === 'click' ? 'pointer' : 'default'
            }}
            onMouseLeave={() => {
              if (hidePopperOn === 'leave') {
                handlePopoverClose();
              }
            }}
            {...slotProps?.surplusWrapper}
          >
            <StyledAvatar size={size} $active={open} $disabled={!showPopup} {...slotProps?.surplus}>
              +{hiddenElements.length}
            </StyledAvatar>
            {showPopup && (
              <Background open={open} backdrop={hidePopperOn === 'click'} onClick={handlePopoverClose}>
                <Popper
                  placement="bottom-start"
                  onClose={() => {
                    handlePopoverClose();
                  }}
                  id={POPOVER_ID}
                  open={open}
                  anchorEl={anchorEl}
                  transition
                  style={{
                    zIndex: 1300
                  }}
                >
                  {/* @ts-ignore all good here*/}
                  {({ TransitionProps }) => (
                    <Fade {...TransitionProps}>
                      <Paper
                        elevation={3}
                        sx={(theme) => ({
                          background: theme.tokens.colorSurface
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <Box
                          padding={popperSpacing}
                          onMouseLeave={() => {
                            if (hidePopperOn === 'leave') {
                              handlePopoverClose();
                            }
                          }}
                        >
                          <Transition
                            open={open}
                            elements={hiddenElements}
                            options={slotProps?.popperElementsAnimation}
                          >
                            {(child, index) => (
                              <Box marginTop={index !== 0 ? popperSpacing : 0} key={index}>
                                {child}
                              </Box>
                            )}
                          </Transition>
                        </Box>
                      </Paper>
                    </Fade>
                  )}
                </Popper>
              </Background>
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

const StyledAvatar = styled(Avatar)<{ $active: boolean; $disabled: boolean }>`
    transition: all 0.2s ease-in-out;
    background-color: ${({ $active, $disabled, theme }) => ($active && !$disabled ? theme.tokens.colorPrimary : undefined)};
    opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
    &:hover {
        background-color: ${({ theme, $disabled }) => (!$disabled ? theme.tokens.colorPrimary : undefined)};
    }
`;

function Background({
  open,
  backdrop,
  children,
  onClick
}: {
  open: boolean;
  backdrop?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  if (backdrop) {
    return (
      <Backdrop open={open} onClick={onClick} invisible style={{ zIndex: 1299, cursor: 'pointer' }}>
        {children}
      </Backdrop>
    );
  }
  return children;
}
